import {
  HTMLAttributes,
  HTMLProps,
  Key,
  ReactNode,
  SyntheticEvent,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { CSVLink } from 'react-csv';
import { useNavigate } from 'react-router-dom';
import {
  DownloadOutlined,
  FilterOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { UseQueryResult } from '@tanstack/react-query';
import {
  Button,
  ButtonProps,
  FloatButtonProps,
  Input,
  InputProps,
  InputRef,
  Space,
  TablePaginationConfig,
  TableProps,
  theme,
  TourProps,
} from 'antd';
import { ColumnType, FilterValue, SorterResult } from 'antd/es/table/interface';
import { debounce } from 'lodash';
import { Resizable } from 're-resizable';

import { CommonFindResult } from '@app/infra-interfaces';
import { formatIsoDate } from '@app/utils-agnostic';

import { useReactiveTableHeight } from './use-reactive-table-height';

const DEFAULT_PAGE_SIZE = 50;

const ResizableTitle = ({
  onResize,
  onResizeStart,
  width,
  ...restProps
}: HTMLAttributes<unknown> & {
  width: number;
  onResize?: (e: MouseEvent | globalThis.TouchEvent, delta: number) => void;
  onResizeStart?: (width: number) => void;
}) => {
  return (
    <Resizable
      as={'th'}
      enable={{ right: !!onResizeStart && !!onResize }}
      size={{ width, height: '100%' }}
      onResizeStart={() => onResizeStart && onResizeStart(width)}
      onResize={(e, direction, ref, d) => {
        onResize && onResize(e, d.width);
      }}
      onResizeStop={() => onResizeStart && onResizeStart(0)}
      {...restProps}
    />
  );
};

const _calculate = (order: unknown, field: string[], index = 0): object => {
  if (index > field.length) {
    throw new Error('Error parsing table direction');
  }

  return {
    [field[index]]: index + 1 === field.length ? order : _calculate(order, field, index + 1),
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const _getObjectProperty = <T,>(item: T, dataIndex: (string | number)[], index: number): any => {
  if (index < 0) {
    return item;
  }

  return _getObjectProperty(item, dataIndex, index - 1)?.[dataIndex[index]];
};

type TableState<T> = {
  columnWidths: Pick<ColumnType<T>, 'width'>[];
  pagination: TablePaginationConfig;
  filters: Record<string, FilterValue | null>;
  sorter: SorterResult<T> | SorterResult<T>[];
  searchText: string;
};

const _reducer = <T,>(
  state: TableState<T>,
  action: { disableSession?: boolean; name: string } & (
    | {
        type: 'onSearchTextChange';
        value: string;
      }
    | {
        type: 'onTableChange';
        value: {
          pagination: TablePaginationConfig;
          filters: Record<string, FilterValue | null>;
          sorter: SorterResult<T> | SorterResult<T>[];
        };
      }
    | {
        type: 'onColumnResize';
        value: {
          index: number;
          width: number;
        };
      }
    | { type: 'set'; value: TableState<T> }
  ),
) => {
  const newState =
    action.type === 'onSearchTextChange'
      ? {
          columnWidths: state.columnWidths,
          pagination: {
            ...state.pagination,
            current: 1,
          },
          searchText: action.value,
          sorter: state.sorter,
          filters: state.filters,
        }
      : action.type === 'onTableChange'
        ? {
            columnWidths: state.columnWidths,
            pagination: {
              ...action.value.pagination,
              current:
                state.pagination.pageSize !== action.value.pagination?.pageSize ? 1 : action.value.pagination.current,
            },
            filters: action.value.filters,
            sorter: action.value.sorter,
            searchText: state.searchText,
          }
        : action.type === 'onColumnResize'
          ? {
              ...state,
              columnWidths: state.columnWidths?.map(({ width, ...x }, index) => ({
                ...x,
                width: action.value.index === index ? action.value.width : width,
              })),
            }
          : action.type === 'set'
            ? action.value
            : state;
  !action.disableSession && window.sessionStorage.setItem(`saaf_table_${action.name}`, JSON.stringify(newState));
  return newState;
};

type TableStateOptions = {
  /**
   * Custom filters for table columns.
   * For nested fields, use period notation for the keys (ex: `parent.child` or `grandparent.parent.child`)
   */
  customFilters?: { [key: string]: (value: boolean | Key | null) => object };

  disableSession?: boolean;

  /**
   * Fields to apply global search over.
   * For nested fields, use period notation (ex: `parent.child` or `grandparent.parent.child`)
   */
  globalSearchFields?: string[];
};

export const useTableState = <T,>(_name: string, columns: ColumnType<T>[], options?: TableStateOptions) => {
  const createButtonRef = useRef<HTMLButtonElement>(null);
  const csvLinkRef = useRef<HTMLButtonElement>(null);
  const reloadButtonRef = useRef<HTMLButtonElement>(null);
  const resetButtonRef = useRef<HTMLButtonElement>(null);
  const searchInputRef = useRef<InputRef>(null);

  // tour props
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  const navigate = useNavigate();
  const name = _name.toLocaleLowerCase().replaceAll(' ', '_');
  const searchFieldText = options?.globalSearchFields
    ?.map((x) =>
      x
        .split('.')
        .slice(-2)
        .map((x) => x.split(/(?=[A-Z]|^[a-z])/).join(' '))
        .join(' ')
        .toLocaleLowerCase(),
    )
    .join(', ');

  const _defaultState: TableState<T> = useMemo(() => {
    const defaultFilters = Object.fromEntries(
      columns.map(({ defaultFilteredValue, dataIndex }) => [
        Array.isArray(dataIndex) ? dataIndex.join('.') : dataIndex,
        defaultFilteredValue,
      ]),
    );
    const defaultSortColumn = columns.find((x) => !!x.defaultSortOrder);
    return {
      columnWidths: columns.map(({ defaultFilteredValue, defaultSortOrder, responsive, width, ...column }) => ({
        width: width ?? 150,
      })),
      filters: defaultFilters,
      pagination: { pageSize: DEFAULT_PAGE_SIZE, current: 1, pageSizeOptions: [10, 25, 50, 100, 500, 1000] },
      searchText: '',
      sorter: defaultSortColumn
        ? { field: defaultSortColumn.dataIndex, order: defaultSortColumn.defaultSortOrder }
        : {},
    };
  }, [columns]);

  const _initializer: (args: TableState<T>) => TableState<T> = useMemo(
    () => (args) => {
      const sessionValue = !options?.disableSession ? window.sessionStorage.getItem(`saaf_table_${name}`) : undefined;

      if (sessionValue) {
        const parsedValues: TableState<T> = JSON.parse(sessionValue);

        if (parsedValues.columnWidths?.length !== columns.length) {
          parsedValues.columnWidths = columns.map((x) => ({ width: x.width ?? 150 }));
        }

        return parsedValues;
      }

      return args;
    },
    [columns, name, options?.disableSession],
  );

  const [current, dispatch] = useReducer(_reducer<T>, _defaultState, _initializer);
  const [searchText, setSearchText] = useState(current.searchText);
  const [startWidth, setStartWidth] = useState<number>(0);
  const { parentRef, toolbarRef, getTableHeight } = useReactiveTableHeight();
  const { token } = theme.useToken();

  const _getOrderBy = (): object | undefined => {
    if (current.sorter && !Array.isArray(current.sorter) && current.sorter.field && current.sorter.order) {
      const order = current.sorter.order === 'ascend' ? 'asc' : 'desc';

      if (typeof current.sorter.field === 'string')
        return {
          [current.sorter.field]: order,
        };

      if (Array.isArray(current.sorter.field)) {
        return _calculate(order, current.sorter.field);
      }
    }

    return undefined;
  };

  const _onSearchInputChange = useMemo(
    () => debounce((value: string) => dispatch({ name, type: 'onSearchTextChange', value }), 600),
    [name],
  );

  const getCSVLinkProps = (
    dataSource?: T[],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    customFormatter?: { [key: string]: (value: any | undefined, record: T) => string },
    children?: ReactNode,
  ) => ({
    children: children ?? (
      <Button ref={csvLinkRef} icon={<DownloadOutlined />}>
        Export to CSV
      </Button>
    ),
    data:
      dataSource?.map((item) => {
        const row: { [key: string]: string | number | boolean | Date | null | undefined } = {};
        columns.forEach((column) => {
          if (typeof column.title === 'string' && column.title !== 'Action') {
            const dataIndex = Array.isArray(column.dataIndex)
              ? column.dataIndex
              : column.dataIndex
                ? [column.dataIndex]
                : [];

            if (dataIndex.length > 0) {
              if (customFormatter && dataIndex.join('.') in customFormatter) {
                row[column.title] = customFormatter[dataIndex.join('.')](
                  _getObjectProperty(item, dataIndex, dataIndex.length - 1),
                  item,
                );
              } else {
                row[column.title] = ['createdOn', 'updatedOn'].includes(dataIndex.join('.'))
                  ? formatIsoDate(_getObjectProperty(item, dataIndex, dataIndex.length - 1).toString())
                  : _getObjectProperty(item, dataIndex, dataIndex.length - 1);
              }
            } else if (customFormatter && (column.title as string) in customFormatter) {
              row[column.title] = customFormatter[column.title](undefined, item);
            }
          }
        });

        return row;
      }) ?? [],
    filename: `${name}_${new Date().toISOString().split('T')[0]}_page_${current.pagination.current}.csv`,
  });

  const getParentLayoutContentProps = () => ({
    ref: parentRef,
    style: {
      paddingTop: 24,
      paddingLeft: 24,
      paddingRight: 24,
      margin: 0,
      height: '100%',
      minHeight: 0,
      background: token.colorBgContainer,
      overflow: 'auto',
    },
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getQuery = (props?: any): any => {
    const OR = (
      options?.globalSearchFields?.map((field: string) => {
        const dataIndexes = field.split('.');
        return _calculate({ contains: current.searchText, mode: 'insensitive' }, dataIndexes);
      }) ?? []
    ).concat(props?.where?.OR ?? []);

    const AND = Object.entries(current.filters)
      .flatMap(([key, values]) => {
        const dataIndexes = key.split('.');

        const OR = values?.map((value) =>
          options?.customFilters?.[key] ? options.customFilters[key](value) : _calculate(value, dataIndexes),
        );
        return OR?.length ? [{ OR }] : [];
      })
      .concat(props?.where?.AND ?? []);

    const take = current.pagination.pageSize ?? DEFAULT_PAGE_SIZE;

    return {
      orderBy: _getOrderBy(),
      skip: ((current.pagination.current ?? 1) - 1) * take,
      take,
      ...props,
      where: {
        ...props?.where,
        AND: AND.length ? AND : undefined,
        OR: OR.length ? OR : undefined,
      },
    };
  };

  const getCreateButtonProps = (props?: ButtonProps): ButtonProps & React.RefAttributes<HTMLElement> => ({
    ...props,
    children: `Create ${_name}`,
    icon: <PlusOutlined />,
    ref: createButtonRef,
    type: 'primary',
    onClick: (e) => {
      navigate('./create');
      props?.onClick && props.onClick(e);
    },
  });

  const getResetFiltersButtonProps = (props?: ButtonProps): ButtonProps & React.RefAttributes<HTMLElement> => ({
    ...props,
    children: 'Reset Table',
    icon: <FilterOutlined />,
    ref: resetButtonRef,
    onClick: (e) => {
      setSearchText('');
      dispatch({ name, type: 'set', value: _defaultState });
      props?.onClick && props.onClick(e);
    },
  });

  const getReloadButtonProps = (): ButtonProps & React.RefAttributes<HTMLElement> => ({
    children: 'Reload',
    icon: <ReloadOutlined />,
    ref: reloadButtonRef,
  });

  const getSearchInputProps = (props?: InputProps): InputProps & React.RefAttributes<InputRef> => ({
    ...props,
    ref: searchInputRef,
    'aria-label': `Search ${_name}`,
    placeholder: `Search${searchFieldText ? `by ${searchFieldText}` : ''}...`,
    style: { ...props?.style, width: 350 },
    value: searchText,
    onChange: (e) => {
      setSearchText(e.target.value);
      _onSearchInputChange(e.target.value);
      props?.onChange && props.onChange(e);
    },
  });

  const getTableProps = (queryResult: UseQueryResult<CommonFindResult<T>>, props?: TableProps<T>): TableProps<T> => {
    return {
      columns: columns.map(({ defaultFilteredValue, defaultSortOrder, responsive, width, ...column }, index) => {
        const columnKey = Array.isArray(column.dataIndex)
          ? column.dataIndex.join('.')
          : (column.dataIndex as string | number | undefined)?.toString() ?? '';

        const filteredValue = current.filters[columnKey] || null;

        const sortOrder =
          !Array.isArray(current.sorter) &&
          current.sorter?.field &&
          (Array.isArray(current.sorter.field) ? current.sorter.field.join('.') : current.sorter.field) === columnKey
            ? current.sorter.order
            : null;

        return {
          ...column,
          filteredValue,
          sortOrder,
          width: current.columnWidths?.[index]?.width,
          onHeaderCell: () =>
            ({
              width: current.columnWidths?.[index]?.width,
              onResizeStart: (width: number) => {
                setStartWidth(width);
              },
              onResize: (e: SyntheticEvent, delta: number) =>
                dispatch({ name, type: 'onColumnResize', value: { index, width: startWidth + delta } }),
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
            }) as any,
        };
      }) as ColumnType<T>[],
      components: { header: { cell: ResizableTitle } },
      size: 'small',
      dataSource: queryResult.data?.data ?? [],
      loading: queryResult.isPending || queryResult.isFetching,
      scroll: {
        y: getTableHeight(),
        ...props?.scroll,
      },
      virtual: true,
      onChange: (
        pagination: TablePaginationConfig,
        filters: Record<string, FilterValue | null>,
        sorter: SorterResult<T> | SorterResult<T>[],
      ) => dispatch({ name, type: 'onTableChange', value: { pagination, filters, sorter } }),
      ...props,
      pagination: {
        ...current.pagination,
        showTotal: (total: number) => (
          <div>
            <b>{total}</b>
            &nbsp; Records
          </div>
        ),
        total: queryResult.data?.count,
        ...props?.pagination,
      },
    };
  };

  type GetToolbarPropsOptions = {
    items?: ReactNode;
    queryResult?: UseQueryResult<CommonFindResult<T>>;
    props?: HTMLProps<HTMLDivElement>;
  };

  const getToolbarProps = ({ items, props, queryResult }: GetToolbarPropsOptions): HTMLProps<HTMLDivElement> => ({
    children: (
      <Space style={{ marginBottom: 20 }} wrap>
        {items ?? (
          <>
            <Input {...getSearchInputProps()} />
            <Button {...getCreateButtonProps()} />
            <Button {...getReloadButtonProps()} onClick={() => queryResult?.refetch()} />
            <Button {...getResetFiltersButtonProps()} />
            <CSVLink {...getCSVLinkProps(queryResult?.data?.data)} />
          </>
        )}
      </Space>
    ),
    ref: toolbarRef,
    style: { ...props?.style, width: '100%' },
  });

  const getTourFloatButtonProps = (): FloatButtonProps => ({
    icon: <QuestionCircleOutlined />,
    tooltip: 'Start a Tour',
    type: 'primary',
    onClick: () => setOpen(true),
  });

  const getTourProps = (_steps: TourProps['steps'] = []): TourProps => {
    const steps: TourProps['steps'] = [
      {
        title: 'Search',
        description: `Use the search input to filter${searchFieldText ? ` by ${searchFieldText}` : ''}.`,
        target: () => searchInputRef.current?.input as HTMLInputElement,
      },
      {
        title: 'Create',
        description: `Click on the "Create ${_name}" button to create a new ${_name.toLowerCase()}.`,
        target: () => createButtonRef.current as HTMLButtonElement,
      },
      {
        title: 'Reload Table',
        description: 'Click on the "Reload" button to refresh the table with the latest data.',
        target: () => reloadButtonRef.current as HTMLButtonElement,
      },
      {
        title: 'Reset Table',
        description:
          'Click on the "Reset Table" button to clear all the filters and search input, as well as reset the table column sizes. If any sorting/filters were set through the link to this page, those settings will be restored instead.',
        target: () => resetButtonRef.current as HTMLButtonElement,
      },
      ...(csvLinkRef.current
        ? [
            {
              title: 'Export to CSV',
              description: 'Click on the "Export to CSV" button to create a CSV file with the data from the table.',
              target: () => csvLinkRef.current as HTMLButtonElement,
            },
          ]
        : []),
      ..._steps,
    ];

    return {
      current: step,
      open,
      steps,
      onChange: (current) => setStep(current),
      onClose: () => setOpen(false),
      onFinish: () => setOpen(false),
    };
  };

  return {
    getCSVLinkProps,
    getQuery,
    getCreateButtonProps,
    getParentLayoutContentProps,
    getResetFiltersButtonProps,
    getReloadButtonProps,
    getSearchInputProps,
    getTableProps,
    getToolbarProps,
    getTourFloatButtonProps,
    getTourProps,
  };
};
